export default defineNuxtRouteMiddleware((to, from) => {
    const url = useRequestURL()
    // Robust host detection for SSR
    const headers = useRequestHeaders(['host', 'x-forwarded-host'])
    const hostHeader = headers['x-forwarded-host'] || headers['host'] || url.hostname

    // Remove port if present
    const host = (hostHeader || '').split(':')[0] as string

    // Get the tenant's active domain to determine what counts as a subdomain.
    // For "creator.psyfeature.lvh.me", if the tenant domain is "psyfeature.lvh.me",
    // the subdomain is "creator", not "psyfeature".
    let tenantDomain: string | undefined
    if (process.server) {
        // During SSR, read directly from the server event context (always available)
        const event = useRequestEvent()
        tenantDomain = event?.context?.tenantDomain
    }
    if (!tenantDomain) {
        // Client-side or SSR fallback: use tenant state from plugin
        const tenant = useState<any>('tenant')
        tenantDomain = tenant.value?.activeDomain
    }

    let detectedSubdomain: string | null = null

    if (tenantDomain && host.endsWith(tenantDomain)) {
        // Strip the tenant domain to get the subdomain prefix. Only the
        // LEFTMOST label counts as the subdomain — when the tenant domain
        // resolves to a parent (e.g. `lvh.me` instead of `diztree.lvh.me`)
        // the raw prefix would be `shop.diztree`, which is not in any
        // reserved list and gets misinterpreted as an artist slug. Using
        // `prefix.split('.')[0]` keeps the well-known subdomain (`shop`,
        // `admin`, `creator`, …) recognizable regardless of how deep the
        // tenant domain ended up resolving.
        const prefix = host.slice(0, -(tenantDomain.length)).replace(/\.$/, '')
        detectedSubdomain = prefix ? prefix.split('.')[0] : null
    } else {
        // Fallback: classic detection for when tenant data isn't loaded yet
        const parts = host.split('.')
        if (parts.length >= 3) {
            detectedSubdomain = parts[0]
        } else if (parts.length === 2 && parts[1] === 'localhost') {
            detectedSubdomain = parts[0]
        }
    }

    // Superadmin subdomain: the panel lives at the ROOT of this subdomain
    // (router.options.ts strips the /superadmin page-tree prefix), so every
    // path here is already a panel / passthrough route — nothing to bounce.
    // Old `/superadmin/*` bookmarks get the now-redundant prefix stripped so
    // they still resolve (e.g. /superadmin/tenants → /tenants).
    if (detectedSubdomain === 'superadmin') {
        const stripped = to.path.replace(/^\/(en|de|pt|es)(?=\/|$)/, '')
        if (stripped === '/superadmin' || stripped.startsWith('/superadmin/')) {
            return navigateTo(stripped.replace('/superadmin', '') || '/')
        }
        // Shared pages reached from the superadmin sidebar — Sign-in &
        // Security (/account/*) and Track Feed (/feed) — declare a
        // creator/default layout in their own definePageMeta. Force the
        // superadmin shell here (before first paint, no flicker) so they
        // render with the panel chrome. Panel pages already declare
        // layout:'superadmin'; pre-session/legal pages (/auth, /forgot-password,
        // …) deliberately keep their own centered layout.
        const wantsPanelChrome =
            stripped === '/account' || stripped.startsWith('/account/')
            || stripped === '/feed' || stripped.startsWith('/feed/')
        if (wantsPanelChrome) {
            setPageLayout('superadmin')
        }
        return
    }

    // Labels subdomain — same page tree as creator (router.options.ts
    // maps labels.* → /creator/*) but we override the layout to render
    // the Label-Manager-focused chrome (sidebar, branding). Pages still
    // declare `definePageMeta({ layout: 'creator' })`; setPageLayout
    // overrides at runtime.
    //
    // Skip layout override for paths that explicitly want their own
    // layout (auth flow + tenant-onboarding gate). Those pages live
    // at the platform-shell layer; rendering them inside the Label
    // Studio chrome would be jarring and the chrome's own permission
    // gates would compete with the gate the page is enforcing.
    if (detectedSubdomain === 'labels') {

        const stripped = to.path.replace(/^\/(en|de|pt|es)(?=\/|$)/, '')
        const isLayoutExempt =
            stripped === '/auth'
            || stripped.startsWith('/auth/')
        if (!isLayoutExempt) {
            setPageLayout('labels')
        }
        return
    }

    // scouts.* subdomain — mirrors the labels.* pattern. Pages declare
    // `definePageMeta({ layout: 'scouts' })` already; this is a safety
    // net so the scout chrome renders even on routes that forgot to set
    // it (and keeps parity with how labels.* overrides at runtime).
    if (detectedSubdomain === 'scouts') {
        const stripped = to.path.replace(/^\/(en|de|pt|es)(?=\/|$)/, '')
        const isLayoutExempt =
            stripped === '/auth'
            || stripped.startsWith('/auth/')
        if (!isLayoutExempt) {
            setPageLayout('scouts')
        }
        return
    }

    // /feed is the one universal page that lives on every subdomain
    // (admin, creator, shop, labels, scouts, apex). Because it's a
    // single file with no `definePageMeta({ layout })`, Nuxt would
    // SSR-render it with the `default` layout (1440px) and then snap
    // to the host-appropriate layout once the page's script setup
    // re-fired `setPageLayout()`. That late switch was the visible
    // "page content gets wider on refresh" flash. Deciding the layout
    // HERE in middleware runs before render on both SSR and client, so
    // the first paint is already in the right layout — no flicker.
    // (labels/scouts already covered by the branches above.)
    const feedStripped = to.path.replace(/^\/(en|de|pt|es)(?=\/|$)/, '')
    if (feedStripped === '/feed' || feedStripped.startsWith('/feed/')) {
        if (detectedSubdomain === 'admin') setPageLayout('admin')
        else if (detectedSubdomain === 'creator') setPageLayout('creator')
        else if (detectedSubdomain === 'shop') setPageLayout('shop')
        // apex (no subdomain) keeps the default layout intentionally.
    }

    // /team is the internal directory — staff-only landing surfaced on
    // both admin.* and creator.* under the same URL. Mirroring the /feed
    // pattern keeps the first paint in the correct chrome (sidebar +
    // header) and avoids the layout-snap flash on refresh.
    if (feedStripped === '/team' || feedStripped.startsWith('/team/')) {
        if (detectedSubdomain === 'admin') setPageLayout('admin')
        else if (detectedSubdomain === 'creator') setPageLayout('creator')
        else if (detectedSubdomain === 'labels') setPageLayout('labels')
        else if (detectedSubdomain === 'scouts') setPageLayout('scouts')
    }

    // Shop chrome for the shared legal/info/account pages — shop.* ONLY.
    // These universal pages declare no layout of their own, so on the shop
    // subdomain they used to render in layouts/default.vue with the
    // marketing LandingNav and no player: a customer clicking "Imprint" in
    // the shop footer fell out of the shop chrome while "Widerrufsrecht"
    // (which declares layout:'shop' itself) kept it. Same mechanism as the
    // /feed block above — decided in middleware so the first paint is
    // already correct on SSR and client. The apex and every panel
    // subdomain are deliberately untouched (about.vue's own
    // setPageLayout branches for admin/creator/labels keep working; it
    // has no shop branch, so this middleware choice stands there).
    if (detectedSubdomain === 'shop') {
        const SHOP_CHROME_PAGES = [
            '/terms', '/imprint', '/datenschutz', '/contact', '/faq',
            '/about', '/data-deletion', '/account'
        ]
        if (SHOP_CHROME_PAGES.some(p => feedStripped === p || feedStripped.startsWith(p + '/'))) {
            setPageLayout('shop')
        }
    }

    // Only handle artist slug detection on index pages
    if (to.path !== '/' && !/^\/(de|pt|es)$/.test(to.path)) return

    // Exclude known subdomains from artist slug detection
    const reservedSubdomains = ['www', 'admin', 'creator', 'labels', 'api', 'app', 'lnk', 'shop', 'scouts', 'superadmin']
    let slug: string | null = null

    if (detectedSubdomain && !reservedSubdomains.includes(detectedSubdomain)) {
        slug = detectedSubdomain
    }

    // Share state between server and client (hydration mismatch fix)
    const artistSlug = useState<string | null>('artistSlug', () => slug)

    // If state was already populated on server (during hydration on client), use it
    if (process.client && artistSlug.value) {
        slug = artistSlug.value
    } else {
        artistSlug.value = slug
    }

    if (slug) {
        setPageLayout('artist-public')
    }
})
